import { useState, type FormEvent } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Pencil, Trash2 } from "lucide-react";

import { eventService, type EventCategory, type EventRegistration, type EventResource, type EventTask, type SchoolEvent } from "../../services/eventService";
import { apiErrorMessage } from "../../utils/apiError";
import { todayIso } from "../../utils/dates";
import AIAssistField from "../AIAssistField";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { Modal } from "../Modal";
import { errorLine } from "../inventory/InventoryParts";
import { SearchPick } from "../library/Pickers";
import { humanise } from "../register/registerLogic";
import { EVENT_TYPES, PRIORITIES, RATINGS, RESOURCE_STATUSES, RESOURCE_TYPES, TASK_STATUSES, clock, day } from "./eventsLogic";

const textOf = (data: FormData) => (name: string) => String(data.get(name) ?? "").trim() || null;

/** A new event (always saved as a draft) or changes to one not yet held or cancelled. */
export function EventForm({ event, categories, onClose, onDone }: {
  event?: SchoolEvent;
  categories: EventCategory[];
  onClose: () => void;
  onDone: (row: SchoolEvent) => void;
}) {
  const [name, setName] = useState(event?.name ?? "");
  const [kind, setKind] = useState(event?.event_type ?? "academic");
  const [category, setCategory] = useState(event ? String(event.category) : "");
  const [start, setStart] = useState(event ? day(event.start_date) : todayIso());
  const [end, setEnd] = useState(event ? day(event.end_date) : todayIso());
  const [startTime, setStartTime] = useState(clock(event?.start_time));
  const [venue, setVenue] = useState(event?.venue ?? "");
  const [description, setDescription] = useState(event?.description ?? "");
  const [registration, setRegistration] = useState(event?.registration_required ?? true);
  const [isPublic, setIsPublic] = useState(event?.is_public ?? true);
  const save = useMutation({
    mutationFn: (data: Record<string, unknown>) => (event ? eventService.updateEvent({ id: event.id, data }) : eventService.createEvent(data)),
    onSuccess: onDone,
  });
  const submit = (formEvent: FormEvent<HTMLFormElement>) => {
    formEvent.preventDefault();
    const text = textOf(new FormData(formEvent.currentTarget));
    const number = (key: string) => (text(key) ? Number(text(key)) : null);
    save.mutate({
      name: name.trim(),
      event_type: kind,
      category: Number(category),
      description: description.trim() || null,
      start_date: start,
      end_date: end < start ? start : end,
      start_time: startTime || null,
      end_time: text("end_time"),
      venue: venue.trim() || null,
      address: text("address"),
      registration_required: registration,
      max_attendees: registration ? number("max_attendees") : null,
      registration_deadline: registration ? text("registration_deadline") : null,
      expected_attendees: number("expected_attendees"),
      contact_person: text("contact_person"),
      contact_email: text("contact_email"),
      contact_phone: text("contact_phone"),
      agenda: text("agenda"),
      requirements: text("requirements"),
      notes: text("notes"),
      is_public: isPublic,
      show_on_sign_in: isPublic && new FormData(formEvent.currentTarget).get("show_on_sign_in") === "on",
    });
  };
  const usable = categories.filter((row) => row.is_active || row.id === event?.category);

  return (
    <FormModal
      id="school-event"
      title={event ? `Change ${event.name}` : "New event"}
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The event could not be saved.") : undefined}
      saveLabel={event ? "Save changes" : "Save as a draft"}
      note={event ? undefined : "Nobody sees it until you publish it."}
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="The event" divided={false}>
        <Field label="Name" className="sm:col-span-2" hint="e.g. Science fair 2026, Visiting day, P7 graduation.">
          <input required maxLength={200} value={name} onChange={(e) => setName(e.target.value)} className="input mt-1" />
        </Field>
        <Field label="Category" hint={usable.length ? undefined : "Add a category first, with Categories beside New event."}>
          <select required value={category} onChange={(e) => setCategory(e.target.value)} className="input mt-1">
            <option value="">Choose a category</option>
            {usable.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
          </select>
        </Field>
        <Field label="Kind">
          <select value={kind} onChange={(e) => setKind(e.target.value)} className="input mt-1">
            {EVENT_TYPES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
        <Field label="Description" optional as="div" className="sm:col-span-2" hint="What happens, for the people invited.">
          <AIAssistField
            rows={3}
            value={description}
            onChange={(next) => setDescription(next)}
            placeholder="e.g. Learners show their science projects to parents and judges; prizes at 3 pm."
            context={{ key: "events.note", params: { kind: "description", name, event_type: kind, category: category || undefined, start_date: start, end_date: end, start_time: startTime || undefined, venue: venue || undefined } }}
            draftLabel="Draft it"
            draftHint="Works from the name, kind, dates and venue above, and your notes. It leaves gaps rather than invent details."
          />
        </Field>
      </FormSection>

      <FormSection title="When and where">
        <Field label="Starts">
          <input required type="date" value={start} onChange={(e) => { setStart(e.target.value); if (end < e.target.value) setEnd(e.target.value); }} className="input mt-1" />
        </Field>
        <Field label="Ends" hint="The same day for a one-day event.">
          <input required type="date" min={start} value={end} onChange={(e) => setEnd(e.target.value)} className="input mt-1" />
        </Field>
        <Field label="From" optional>
          <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="input mt-1" />
        </Field>
        <Field label="Until" optional>
          <input type="time" name="end_time" defaultValue={clock(event?.end_time)} className="input mt-1" />
        </Field>
        <Field label="Venue" optional hint="e.g. Main hall, the school field.">
          <input maxLength={200} value={venue} onChange={(e) => setVenue(e.target.value)} className="input mt-1" />
        </Field>
        <Field label="Address" optional hint="Only when it is away from school.">
          <input name="address" defaultValue={event?.address ?? ""} className="input mt-1" />
        </Field>
      </FormSection>

      <FormSection title="Coming">
        <label className="flex items-start gap-2 text-sm text-gray-700 sm:col-span-2">
          <input type="checkbox" checked={registration} onChange={(e) => setRegistration(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300" />
          <span>
            <span className="font-medium">People register to come</span>
            <span className="block text-xs text-gray-500">Gives a guest list to check people in at the door. Leave it off for events everyone simply attends.</span>
          </span>
        </label>
        {registration && (
          <>
            <Field label="Places" optional hint="Leave empty for no limit.">
              <input type="number" min={1} name="max_attendees" defaultValue={event?.max_attendees ?? ""} className="input mt-1" />
            </Field>
            <Field label="Registration closes" optional hint="On or before the day it starts.">
              <input type="date" name="registration_deadline" max={start} defaultValue={day(event?.registration_deadline)} className="input mt-1" />
            </Field>
          </>
        )}
        <Field label="Expected turnout" optional hint="A planning figure, e.g. for catering.">
          <input type="number" min={1} name="expected_attendees" defaultValue={event?.expected_attendees ?? ""} className="input mt-1" />
        </Field>
      </FormSection>

      <FormSection title="Programme and contact">
        <Field label="Programme" optional className="sm:col-span-2" hint="The running order, one item per line.">
          <textarea name="agenda" rows={3} defaultValue={event?.agenda ?? ""} className="input mt-1" />
        </Field>
        <Field label="What to bring or wear" optional className="sm:col-span-2">
          <textarea name="requirements" rows={2} defaultValue={event?.requirements ?? ""} className="input mt-1" />
        </Field>
        <Field label="Contact person" optional>
          <input name="contact_person" maxLength={100} defaultValue={event?.contact_person ?? ""} className="input mt-1" />
        </Field>
        <Field label="Contact phone" optional>
          <input type="tel" name="contact_phone" maxLength={20} defaultValue={event?.contact_phone ?? ""} className="input mt-1" />
        </Field>
        <Field label="Contact email" optional>
          <input type="email" name="contact_email" defaultValue={event?.contact_email ?? ""} className="input mt-1" />
        </Field>
        <Field label="Staff notes" optional hint="Only staff see these.">
          <input name="notes" defaultValue={event?.notes ?? ""} className="input mt-1" />
        </Field>
      </FormSection>

      <FormSection title="Who sees it">
        <label className="flex items-start gap-2 text-sm text-gray-700 sm:col-span-2">
          <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300" />
          <span>
            <span className="font-medium">Parents and learners can see it</span>
            <span className="block text-xs text-gray-500">Leave it off for a staff-only event, e.g. a staff meeting.</span>
          </span>
        </label>
        {isPublic && (
          <label className="flex items-start gap-2 text-sm text-gray-700 sm:col-span-2">
            <input type="checkbox" name="show_on_sign_in" defaultChecked={event?.show_on_sign_in ?? false} className="mt-0.5 h-4 w-4 rounded border-gray-300" />
            <span>
              <span className="font-medium">Show it on the sign-in page</span>
              <span className="block text-xs text-gray-500">Anyone can see the sign-in page without an account. It shows once published, until the event ends.</span>
            </span>
          </label>
        )}
      </FormSection>
    </FormModal>
  );
}

/** The categories events are filed under. */
export function EventCategoriesModal({ categories, onClose, onChanged }: { categories: EventCategory[]; onClose: () => void; onChanged: () => void }) {
  const [editing, setEditing] = useState<EventCategory | null>(null);
  const [name, setName] = useState("");
  const [color, setColor] = useState("#2563eb");
  const reset = () => {
    setEditing(null);
    setName("");
    setColor("#2563eb");
  };
  const save = useMutation({
    mutationFn: () => (editing ? eventService.updateCategory({ id: editing.id, data: { name: name.trim(), color } }) : eventService.createCategory({ name: name.trim(), color, is_active: true })),
    onSuccess: () => {
      reset();
      onChanged();
    },
  });
  const toggle = useMutation({ mutationFn: (row: EventCategory) => eventService.updateCategory({ id: row.id, data: { is_active: !row.is_active } }), onSuccess: () => onChanged() });
  const remove = useMutation({ mutationFn: eventService.deleteCategory, onSuccess: () => onChanged() });
  const sorted = [...categories].sort((a, b) => Number(b.is_active) - Number(a.is_active) || a.name.localeCompare(b.name));

  return (
    <Modal isOpen size="2xl" title="Event categories" onClose={onClose} footer={<button type="button" className="btn btn-outline" onClick={onClose}>Close</button>}>
      <div className="space-y-5">
        <p className="text-sm text-gray-600">Categories group the calendar, e.g. Parents, Academic, Sports, Chapel. One with events filed under it can be stopped but not deleted.</p>
        {sorted.length ? (
          <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
            {sorted.map((row) => (
              <li key={row.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                <span className="flex min-w-0 items-center gap-2">
                  <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: row.color }} aria-hidden />
                  <span className={`font-medium ${row.is_active ? "text-gray-900" : "text-gray-400 line-through"}`}>{row.name}</span>
                  <span className="text-xs text-gray-500">{row.event_count} {row.event_count === 1 ? "event" : "events"}</span>
                </span>
                <span className="flex items-center gap-1">
                  <button type="button" disabled={toggle.isPending} onClick={() => toggle.mutate(row)} className="rounded px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100">
                    {row.is_active ? "Stop using" : "Use again"}
                  </button>
                  <button type="button" onClick={() => { setEditing(row); setName(row.name); setColor(row.color); }} className="rounded p-1.5 text-gray-500 hover:bg-gray-100" aria-label={`Edit ${row.name}`}>
                    <Pencil className="h-4 w-4" />
                  </button>
                  {!row.event_count && (
                    <button type="button" disabled={remove.isPending} onClick={() => remove.mutate(row.id)} className="rounded p-1.5 text-red-600 hover:bg-red-50" aria-label={`Delete ${row.name}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">No categories yet. Add the first one below.</p>
        )}
        <form
          className="space-y-3 rounded-lg border border-gray-200 p-4"
          onSubmit={(formEvent) => {
            formEvent.preventDefault();
            save.mutate();
          }}
        >
          <h4 className="text-sm font-semibold text-gray-900">{editing ? `Change ${editing.name}` : "Add a category"}</h4>
          <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
            <Field label="Name">
              <input required maxLength={100} value={name} onChange={(e) => setName(e.target.value)} className="input mt-1" />
            </Field>
            <Field label="Colour" hint="Marks it in the calendar.">
              <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="mt-1 h-10 w-16 rounded border border-gray-300" />
            </Field>
          </div>
          <div className="flex justify-end gap-2">
            {editing && <button type="button" onClick={reset} className="btn btn-outline">Cancel</button>}
            <button disabled={save.isPending || !name.trim()} className="btn btn-primary">{editing ? "Save" : "Add it"}</button>
          </div>
          {[save, toggle, remove].map((mutation, index) => mutation.isError && <div key={index}>{errorLine(mutation.error, "That could not be saved.")}</div>)}
        </form>
      </div>
    </Modal>
  );
}

/** Signing oneself up, with anything the organisers should know. */
export function RegisterForm({ event, onClose, onDone }: { event: SchoolEvent; onClose: () => void; onDone: (row: EventRegistration) => void }) {
  const save = useMutation({ mutationFn: eventService.register, onSuccess: onDone });
  const submit = (formEvent: FormEvent<HTMLFormElement>) => {
    formEvent.preventDefault();
    const text = textOf(new FormData(formEvent.currentTarget));
    save.mutate({ event: event.id, dietary_requirements: text("dietary_requirements"), special_requests: text("special_requests"), emergency_contact: text("emergency_contact"), emergency_phone: text("emergency_phone") });
  };
  return (
    <FormModal
      id="event-registration"
      title={`Register for ${event.name}`}
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The registration could not be made.") : undefined}
      saveLabel="Register"
      note="You can withdraw until the day it starts."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="Anything the organisers should know" divided={false}>
        <Field label="Food needs" optional hint="e.g. vegetarian, no nuts.">
          <input name="dietary_requirements" className="input mt-1" />
        </Field>
        <Field label="Other requests" optional hint="e.g. a wheelchair space, arriving late.">
          <input name="special_requests" className="input mt-1" />
        </Field>
        <Field label="Emergency contact" optional>
          <input name="emergency_contact" maxLength={200} className="input mt-1" />
        </Field>
        <Field label="Their phone" optional>
          <input type="tel" name="emergency_phone" maxLength={20} className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

const RatingSelect = ({ name, label, required }: { name: string; label: string; required?: boolean }) => (
  <Field label={label} optional={!required}>
    <select name={name} required={required} className="input mt-1">
      <option value="">{required ? "Choose a rating" : "No rating"}</option>
      {RATINGS.map(([value, text]) => <option key={value} value={value}>{"★".repeat(Number(value))} {text}</option>)}
    </select>
  </Field>
);

/** Someone's own view of an event they registered for. */
export function FeedbackForm({ registration, onClose, onDone }: { registration: EventRegistration; onClose: () => void; onDone: () => void }) {
  const save = useMutation({ mutationFn: eventService.createFeedback, onSuccess: onDone });
  const submit = (formEvent: FormEvent<HTMLFormElement>) => {
    formEvent.preventDefault();
    const data = new FormData(formEvent.currentTarget);
    const text = textOf(data);
    const rating = (key: string) => (text(key) ? Number(text(key)) : null);
    save.mutate({
      event: registration.event,
      overall_rating: rating("overall_rating"),
      organization_rating: rating("organization_rating"),
      content_rating: rating("content_rating"),
      venue_rating: rating("venue_rating"),
      comments: text("comments"),
      suggestions: text("suggestions"),
      would_attend_again: data.get("would_attend_again") === "on",
      would_recommend: data.get("would_recommend") === "on",
    });
  };
  return (
    <FormModal
      id="event-feedback"
      title={`How was ${registration.event_name}?`}
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The feedback could not be sent.") : undefined}
      saveLabel="Send feedback"
      note="Staff organising events read it. It cannot be changed once sent."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="Ratings" divided={false}>
        <RatingSelect name="overall_rating" label="Overall" required />
        <RatingSelect name="organization_rating" label="How it was organised" />
        <RatingSelect name="content_rating" label="What happened on the day" />
        <RatingSelect name="venue_rating" label="The venue" />
      </FormSection>
      <FormSection title="In your words">
        <Field label="Comments" optional className="sm:col-span-2">
          <textarea name="comments" rows={3} className="input mt-1" />
        </Field>
        <Field label="Suggestions for next time" optional className="sm:col-span-2">
          <textarea name="suggestions" rows={2} className="input mt-1" />
        </Field>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" name="would_attend_again" defaultChecked className="h-4 w-4 rounded border-gray-300" /> I would come again
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" name="would_recommend" defaultChecked className="h-4 w-4 rounded border-gray-300" /> I would recommend it
        </label>
      </FormSection>
    </FormModal>
  );
}

/** A planning task for an event, given to one of the teaching staff. */
export function TaskForm({ task, events, eventId, initialStatus, onClose, onDone }: {
  task?: EventTask;
  /** "completed" when opened to mark the task done. */
  initialStatus?: string;
  events: SchoolEvent[];
  /** Fixed when adding from an event's own panel. */
  eventId?: number;
  onClose: () => void;
  onDone: (row: EventTask) => void;
}) {
  const assignees = useQuery({ queryKey: ["events", "assignees"], queryFn: eventService.getAssignees, staleTime: 5 * 60_000 });
  const [status, setStatus] = useState<string>(initialStatus ?? task?.status ?? "pending");
  const [assignee, setAssignee] = useState(task ? String(task.assigned_to) : "");
  const [completion, setCompletion] = useState(task?.completion_notes ?? "");
  const save = useMutation({
    mutationFn: (data: Record<string, unknown>) => (task ? eventService.updateTask({ id: task.id, data }) : eventService.createTask(data)),
    onSuccess: onDone,
  });
  const open = events.filter((row) => row.status !== "cancelled" && row.status !== "completed");
  const submit = (formEvent: FormEvent<HTMLFormElement>) => {
    formEvent.preventDefault();
    const text = textOf(new FormData(formEvent.currentTarget));
    save.mutate({
      ...(task ? {} : { event: eventId ?? Number(text("event")) }),
      title: text("title"),
      description: text("description"),
      assigned_to: Number(assignee),
      priority: text("priority"),
      due_date: text("due_date"),
      due_time: text("due_time"),
      status,
      completion_notes: status === "completed" ? completion.trim() || null : task?.completion_notes ?? null,
    });
  };
  const forEvent = events.find((row) => row.id === (task?.event ?? eventId));
  return (
    <FormModal
      id="event-task"
      title={task ? `Change: ${task.title}` : "New task"}
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The task could not be saved.") : undefined}
      saveLabel={task ? "Save changes" : "Add the task"}
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="The task" divided={false}>
        {forEvent ? (
          <Field label="Event" as="div">
            <p className="mt-1 rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-800">{forEvent.name} · {day(forEvent.start_date)}</p>
          </Field>
        ) : (
          <Field label="Event">
            <select required name="event" className="input mt-1">
              <option value="">Choose an event</option>
              {open.map((row) => <option key={row.id} value={row.id}>{row.name} · {day(row.start_date)}</option>)}
            </select>
          </Field>
        )}
        <Field label="Task" hint="e.g. Book the hall, Order the tents, Print programmes.">
          <input required name="title" maxLength={200} defaultValue={task?.title ?? ""} className="input mt-1" />
        </Field>
        <Field label="Given to" as="div" hint="Teaching staff, who can see this page.">
          <SearchPick
            required
            items={assignees.data ?? []}
            value={assignee}
            onChange={setAssignee}
            label={(row) => row.full_name}
            detail={(row) => humanise(row.role)}
            placeholder={assignees.isLoading ? "Loading staff…" : "Search by name or role"}
            empty="No one matches"
          />
        </Field>
        <Field label="Priority">
          <select name="priority" defaultValue={task?.priority ?? "medium"} className="input mt-1">
            {PRIORITIES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
        <Field label="Due" optional>
          <input type="date" name="due_date" defaultValue={day(task?.due_date)} max={forEvent ? day(forEvent.end_date) : undefined} className="input mt-1" />
        </Field>
        <Field label="By" optional>
          <input type="time" name="due_time" defaultValue={clock(task?.due_time)} className="input mt-1" />
        </Field>
        <Field label="Details" optional className="sm:col-span-2">
          <textarea name="description" rows={2} defaultValue={task?.description ?? ""} className="input mt-1" />
        </Field>
        {task && (
          <Field label="Where it stands">
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="input mt-1">
              {TASK_STATUSES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
            </select>
          </Field>
        )}
        {task && status === "completed" && (
          <Field label="How it was done" optional as="div" className="sm:col-span-2">
            <AIAssistField
              rows={2}
              value={completion}
              onChange={(next) => setCompletion(next)}
              placeholder="e.g. Hall booked for 8 am to 5 pm; the key is with the bursar."
              context={{ key: "events.note", params: { kind: "task", id: task.id } }}
              draftLabel="Draft it"
              draftHint="Jot what was done first, and it writes it up. It adds nothing you did not say."
            />
          </Field>
        )}
      </FormSection>
    </FormModal>
  );
}

/** Something an event needs: a venue, tents, catering, transport. */
export function ResourceForm({ resource, eventId, onClose, onDone }: { resource?: EventResource; eventId: number; onClose: () => void; onDone: () => void }) {
  const save = useMutation({
    mutationFn: (data: Record<string, unknown>) => (resource ? eventService.updateResource({ id: resource.id, data }) : eventService.createResource(data)),
    onSuccess: onDone,
  });
  const submit = (formEvent: FormEvent<HTMLFormElement>) => {
    formEvent.preventDefault();
    const text = textOf(new FormData(formEvent.currentTarget));
    save.mutate({
      ...(resource ? {} : { event: eventId }),
      name: text("name"),
      resource_type: text("resource_type"),
      quantity: Number(text("quantity") ?? 1),
      unit: text("unit") ?? "piece",
      cost: text("cost"),
      supplier: text("supplier"),
      contact_person: text("contact_person"),
      contact_phone: text("contact_phone"),
      status: text("status"),
      notes: text("notes"),
    });
  };
  return (
    <FormModal
      id="event-resource"
      title={resource ? `Change: ${resource.name}` : "Add something the event needs"}
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "It could not be saved.") : undefined}
      saveLabel={resource ? "Save changes" : "Add it"}
      note="A planning list: money is paid through the finance pages."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="What is needed" divided={false}>
        <Field label="Item" hint="e.g. Tents, PA system, Lunch for guests, School bus.">
          <input required name="name" maxLength={200} defaultValue={resource?.name ?? ""} className="input mt-1" />
        </Field>
        <Field label="Kind">
          <select name="resource_type" defaultValue={resource?.resource_type ?? "equipment"} className="input mt-1">
            {RESOURCE_TYPES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
        <Field label="How many">
          <input required type="number" min={1} name="quantity" defaultValue={resource?.quantity ?? 1} className="input mt-1" />
        </Field>
        <Field label="Counted in" hint="e.g. piece, plate, trip.">
          <input name="unit" maxLength={50} defaultValue={resource?.unit ?? "piece"} className="input mt-1" />
        </Field>
        <Field label="Estimated cost (UGX)" optional hint="For all of them together.">
          <input type="number" min={0} step="1" name="cost" defaultValue={resource?.cost ?? ""} className="input mt-1" />
        </Field>
        <Field label="Where it stands">
          <select name="status" defaultValue={resource?.status ?? "required"} className="input mt-1">
            {RESOURCE_STATUSES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
      </FormSection>
      <FormSection title="Supplier">
        <Field label="Supplier" optional>
          <input name="supplier" maxLength={200} defaultValue={resource?.supplier ?? ""} className="input mt-1" />
        </Field>
        <Field label="Contact person" optional>
          <input name="contact_person" maxLength={100} defaultValue={resource?.contact_person ?? ""} className="input mt-1" />
        </Field>
        <Field label="Contact phone" optional>
          <input type="tel" name="contact_phone" maxLength={20} defaultValue={resource?.contact_phone ?? ""} className="input mt-1" />
        </Field>
        <Field label="Notes" optional>
          <input name="notes" defaultValue={resource?.notes ?? ""} className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}
